import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  Modifier,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CommandMenu } from "@/components/editor/CommandMenu";
import { SortableBlock } from "@/components/editor/SortableBlock";
import { toast } from "sonner";
import { generateId } from '@/lib/utils/uuid';
import { useZoom } from "@/providers/ZoomProvider";
import { manualHighlightExclusionsService } from "@/lib/services/ManualHighlightExclusionsService";
import { useEditorHistory } from "@/hooks/useEditorHistory";
import { getCursorPosition } from "@/components/editor/utils/cursorUtils";
import { getCachedTypes, typesStore } from "@/lib/stores/typesStore";
import { getCachedLibraryItems } from "@/lib/stores/objects/cache/byType";
import { objectsStore } from "@/lib/stores/objects/store";

// Core data structure for editor blocks
export interface Block {
  id: string;
  type: string;
  content: string;
  metadata?: Record<string, any>;
}

// Definition for custom block types that can be added to the editor
export interface CustomBlockDefinition {
  type: string;
  label: string;
  icon: React.ReactNode;
  description: string;
  component: React.ComponentType<CustomBlockProps & Record<string, any>>;
  initialMetadata?: Record<string, any>;
  additionalProps?: Record<string, any>;
}

// Props interface for custom block components
export interface CustomBlockProps {
  id: string;
  block: Block;
  isActive?: boolean;
  onUpdate: (id: string, updates: Partial<Block>) => void;
  onDelete: (id: string) => void;
  onFocus: (id: string) => void;
}

// Main props for the Editor component
interface EditorProps {
  customBlocks?: CustomBlockDefinition[];
  onChange?: (blocks: Block[]) => void;
  initialBlocks?: Block[];
  gameId?: string;
  /** Whether the editor is editable. When false, content cannot be modified. Defaults to true. */
  editable?: boolean;
  // New props for document integration
  onDocumentContentUpdate?: (blockId: string, glossaryItemId: string, glossaryItemName: string) => void;
  onDocumentSave?: () => void;
  onDocumentBlockDelete?: (blockId: string) => void;
  onDocumentBlockCancel?: (blockId: string) => void;
}

// Main Editor component - provides a block-based rich text editor with drag-and-drop functionality
export const Editor = ({ 
  customBlocks = [], 
  onChange, 
  initialBlocks,
  gameId,
  editable = true,
  onDocumentContentUpdate,
  onDocumentSave,
  onDocumentBlockDelete,
  onDocumentBlockCancel,
}: EditorProps = {}) => {
  // Get platform zoom to adjust drag-and-drop coordinates
  const { zoom } = useZoom();

  // Variable reference data - fetch types and objects for {{ key.name }} autocomplete
  // Subscribe to store changes so data refreshes when objects/types are loaded
  const [storeVersion, setStoreVersion] = useState(0);
  useEffect(() => {
    const unsub1 = typesStore.subscribe((_data) => setStoreVersion(v => v + 1));
    const unsub2 = objectsStore.subscribe((_data) => setStoreVersion(v => v + 1));
    return () => { unsub1(); unsub2(); };
  }, []);

  const variableTypes = useMemo(() => gameId ? getCachedTypes(gameId) : [], [gameId, storeVersion]);
  const variableObjects = useMemo(() => gameId ? getCachedLibraryItems(gameId) : [], [gameId, storeVersion]);
  const enableVariableReferences = !!(gameId && (variableTypes.length > 0 || variableObjects.length > 0));

  // Initialize blocks state - use initialBlocks if provided and not empty, otherwise create a default text block
  const [blocks, setBlocks] = useState<Block[]>(
    initialBlocks && initialBlocks.length > 0 ? initialBlocks : [{ id: '1', type: 'text', content: '' }]
  );

  // Track which block is currently active/focused
  const [activeBlockId, setActiveBlockId] = useState('1');
  
  // #region agent log
  const prevBlockCountRef = useRef(blocks.length);
  useEffect(() => {
    if (blocks.length !== prevBlockCountRef.current) {
      if(import.meta.env.DEV){fetch('http://127.0.0.1:7242/ingest/8fd71a2b-62c7-4902-9a51-278ccbdfc26a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Editor.tsx:blockCountChange',message:'BLOCK COUNT CHANGED',data:{prev:prevBlockCountRef.current,now:blocks.length,blockIds:blocks.map(b=>b.id),blockTypes:blocks.map(b=>b.type)},timestamp:Date.now(),hypothesisId:'H-B'})}).catch(()=>{});}
      prevBlockCountRef.current = blocks.length;
    }
  }, [blocks]);
  // #endregion
  
  // Initialize editor history for undo/redo
  const history = useEditorHistory({ maxHistorySize: 50, debounceMs: 500 });
  
  // Ref to track if we're applying history (to avoid pushing during undo/redo)
  const isApplyingHistoryRef = useRef(false);
  
  // Track which block is being dragged for preview
  const [activeBlock, setActiveBlock] = useState<Block | null>(null);
  
  // Ref to track the previous initialBlocks value for comparison
  const prevInitialBlocksRef = useRef<Block[]>();
  
  // Ref to track if we're updating from props (to avoid calling onChange in that case)
  const isUpdatingFromPropsRef = useRef(false);
  
  // Ref for the blocks container (for cross-block selection)
  const blocksContainerRef = useRef<HTMLDivElement>(null);
  
  // State for tracking multi-block selection
  const [multiBlockSelection, setMultiBlockSelection] = useState<{
    isSelecting: boolean;
    isDragging: boolean;  // True when actually dragging (not just mousedown)
    startBlockId: string | null;
    endBlockId: string | null;
    startPosition: { x: number; y: number } | null;
  }>({
    isSelecting: false,
    isDragging: false,
    startBlockId: null,
    endBlockId: null,
    startPosition: null,
  });
  
  // When dragging across blocks, we disable contentEditable to allow native selection
  const isInSelectionMode = multiBlockSelection.isDragging;
  
  // Update blocks when initialBlocks prop changes (e.g., when loading saved content)
  useEffect(() => {
    if (initialBlocks && prevInitialBlocksRef.current !== initialBlocks) {
      // Deep comparison to avoid unnecessary updates
      const currentBlocksString = JSON.stringify(blocks);
      const newBlocksString = JSON.stringify(initialBlocks);
      
   
      
      if (currentBlocksString !== newBlocksString) {
        // #region agent log
        if(import.meta.env.DEV){fetch('http://127.0.0.1:7242/ingest/8fd71a2b-62c7-4902-9a51-278ccbdfc26a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Editor.tsx:initialBlocksSync',message:'BLOCKS RESET from initialBlocks',data:{blocksLen:blocks?.length,initialBlocksLen:initialBlocks?.length,diff:true},timestamp:Date.now(),hypothesisId:'H-B'})}).catch(()=>{});}
        // #endregion
        isUpdatingFromPropsRef.current = true;
        setBlocks(initialBlocks);
        
        // Clear history when loading new document content
        history.clear();
        
        // Restore highlight exclusions from block metadata
        initialBlocks.forEach(block => {
          const exclusions = block.metadata?.highlightExclusions;
          if (exclusions && Array.isArray(exclusions)) {
            exclusions.forEach(key => {
              const parts = key.split('|');
              if (parts.length === 3) {
                const [text, itemId, positionStr] = parts;
                const position = parseInt(positionStr, 10);
                if (!isNaN(position)) {
                  manualHighlightExclusionsService.addExclusion(block.id, text, itemId, position);
                }
              }
            });
          }
        });
        
        // Set the first block as active if there are blocks
        if (initialBlocks.length > 0) {
          setActiveBlockId(initialBlocks[0].id);
        }
        
        // Reset the flag after state update completes
        setTimeout(() => {
          isUpdatingFromPropsRef.current = false;
        }, 0);
      } else {
        console.log('Editor: Blocks are the same, no update needed');
      }
      
      // Update the ref to track the current value
      prevInitialBlocksRef.current = initialBlocks;
    }
  }, [initialBlocks, blocks, history]);
  
  // NOTE: onChange is called inline in every setBlocks updater (except initialBlocks sync
  // which is guarded by isUpdatingFromPropsRef). A previous useEffect [blocks, onChange]
  // was removed because it fired a SECOND onChange call per blocks-change, causing
  // duplicate updateBlocks → setLastActivityTimestamp → re-render cascades.
  
  // State for the command menu (appears when typing '/')
  const [commandMenu, setCommandMenu] = useState<{
    isOpen: boolean;
    blockId: string;
    position: { x: number; y: number };
  }>({
    isOpen: false,
    blockId: '',
    position: { x: 0, y: 0 }
  });
  
  // State to track pending cursor position for a block (e.g., after backspace merges blocks)
  const [pendingCursorPosition, setPendingCursorPosition] = useState<{
    blockId: string;
    position: 'end' | number;
  } | null>(null);

  // Configure drag-and-drop sensors for keyboard and mouse interactions
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Custom modifier to adjust drag coordinates for platform zoom
  // When CSS zoom is applied to the container, dnd-kit's calculations are off
  const zoomModifier: Modifier = useMemo(() => {
    return ({ transform }) => {
      return {
        ...transform,
        x: transform.x / zoom,
        y: transform.y / zoom,
      };
    };
  }, [zoom]);

  const modifiers = useMemo(() => [zoomModifier], [zoomModifier]);

  // Helper to get cursor position from active block
  const getCurrentCursorPosition = useCallback((): number => {
    const activeBlockElement = document.querySelector(
      `[data-block-id="${activeBlockId}"] [contenteditable="true"]`
    ) as HTMLDivElement | null;
    
    if (activeBlockElement) {
      return getCursorPosition(activeBlockElement);
    }
    return 0;
  }, [activeBlockId]);

  // Track previous blocks for change detection
  const prevBlocksRef = useRef<Block[]>(blocks);
  
  // Push current state to history when blocks change
  useEffect(() => {
    // Skip if we're applying history or updating from props
    if (isApplyingHistoryRef.current || isUpdatingFromPropsRef.current) {
      return;
    }
    
    // Skip if blocks are empty or in initial state
    if (blocks.length === 0) {
      return;
    }
    
    // Skip if blocks haven't actually changed (avoid infinite loops)
    if (prevBlocksRef.current === blocks) {
      return;
    }
    
    prevBlocksRef.current = blocks;
    
    // Use queueMicrotask to push state after render completes and avoid React warnings
    queueMicrotask(() => {
      if (!isApplyingHistoryRef.current && !isUpdatingFromPropsRef.current) {
        const cursorPosition = getCurrentCursorPosition();
        history.pushState(blocks, activeBlockId, cursorPosition, false);
      }
    });
  }, [blocks, activeBlockId, history, getCurrentCursorPosition]);

  // Helper to restore cursor position in a block
  const restoreCursorInBlock = useCallback((blockId: string, cursorPosition: number) => {
    const blockElement = document.querySelector(
      `[data-block-id="${blockId}"] [contenteditable="true"]`
    ) as HTMLDivElement | null;
    
    if (!blockElement) {
      console.log('Block element not found for ID:', blockId);
      return false;
    }
    
    blockElement.focus();
    
    // Handle empty blocks
    if (blockElement.textContent === '' || blockElement.textContent === null) {
      const selection = window.getSelection();
      if (selection) {
        const range = document.createRange();
        range.selectNodeContents(blockElement);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      return true;
    }
    
    // Try to restore cursor position
    const selection = window.getSelection();
    if (selection) {
      const walker = document.createTreeWalker(
        blockElement,
        NodeFilter.SHOW_TEXT,
        null
      );
      
      let currentOffset = 0;
      let targetNode: Node | null = null;
      let targetOffset = 0;
      let node;
      let lastNode: Node | null = null;
      
      while ((node = walker.nextNode()) !== null) {
        lastNode = node;
        const nodeLength = node.textContent?.length || 0;
        if (currentOffset + nodeLength >= cursorPosition) {
          targetNode = node;
          targetOffset = Math.min(cursorPosition - currentOffset, nodeLength);
          break;
        }
        currentOffset += nodeLength;
      }
      
      // If we didn't find the exact position, use the last text node
      if (!targetNode && lastNode) {
        targetNode = lastNode;
        targetOffset = lastNode.textContent?.length || 0;
      }
      
      if (targetNode) {
        try {
          const range = document.createRange();
          range.setStart(targetNode, targetOffset);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
          return true;
        } catch (error) {
          console.log('Error setting cursor position:', error);
          // Fallback: set cursor to end of block
          const range = document.createRange();
          range.selectNodeContents(blockElement);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
    }
    
    return false;
  }, []);

  // Handle undo operation
  const handleUndo = useCallback(() => {
    if (!history.canUndo) return;
    
    isApplyingHistoryRef.current = true;
    
    // Save current cursor position to try to maintain it
    const currentCursorPosition = getCurrentCursorPosition();
    
    // Save current state to redo stack before undoing
    const currentSnapshot = {
      blocks,
      activeBlockId,
      cursorPosition: currentCursorPosition,
      timestamp: Date.now(),
    };
    history.saveToRedoStack(currentSnapshot);
    
    const previousSnapshot = history.undo();
    
    if (previousSnapshot) {
      setBlocks(previousSnapshot.blocks);
      onChange?.(previousSnapshot.blocks);
      
      // Try to keep the cursor in the same block if it still exists
      const currentBlockStillExists = previousSnapshot.blocks.some(b => b.id === activeBlockId);
      const targetBlockId = currentBlockStillExists 
        ? activeBlockId 
        : previousSnapshot.blocks[0]?.id || previousSnapshot.activeBlockId;
      
      setActiveBlockId(targetBlockId);
      
      // Restore cursor position after DOM updates
      setTimeout(() => {
        // Try to maintain the current cursor position if we're in the same block
        const positionToRestore = currentBlockStillExists ? currentCursorPosition : previousSnapshot.cursorPosition;
        restoreCursorInBlock(targetBlockId, positionToRestore);
        // Clear the flag after a longer delay to ensure useEffect's setTimeout has checked it
        setTimeout(() => {
          isApplyingHistoryRef.current = false;
        }, 10);
      }, 0);
    } else {
      isApplyingHistoryRef.current = false;
    }
  }, [history, blocks, activeBlockId, getCurrentCursorPosition, restoreCursorInBlock, onChange]);

  // Handle redo operation
  const handleRedo = useCallback(() => {
    if (!history.canRedo) return;
    
    isApplyingHistoryRef.current = true;
    
    // Save current cursor position to try to maintain it
    const currentCursorPosition = getCurrentCursorPosition();
    
    // Save current state to undo stack before redoing
    const currentSnapshot = {
      blocks,
      activeBlockId,
      cursorPosition: currentCursorPosition,
      timestamp: Date.now(),
    };
    history.saveToUndoStack(currentSnapshot);
    
    const nextSnapshot = history.redo();
    
    if (nextSnapshot) {
      setBlocks(nextSnapshot.blocks);
      onChange?.(nextSnapshot.blocks);
      
      // Try to keep the cursor in the same block if it still exists
      const currentBlockStillExists = nextSnapshot.blocks.some(b => b.id === activeBlockId);
      const targetBlockId = currentBlockStillExists 
        ? activeBlockId 
        : nextSnapshot.blocks[0]?.id || nextSnapshot.activeBlockId;
      
      setActiveBlockId(targetBlockId);
      
      // Restore cursor position after DOM updates
      setTimeout(() => {
        // Try to maintain the current cursor position if we're in the same block
        const positionToRestore = currentBlockStillExists ? currentCursorPosition : nextSnapshot.cursorPosition;
        restoreCursorInBlock(targetBlockId, positionToRestore);
        // Clear the flag after a longer delay to ensure useEffect's setTimeout has checked it
        setTimeout(() => {
          isApplyingHistoryRef.current = false;
        }, 10);
      }, 0);
    } else {
      isApplyingHistoryRef.current = false;
    }
  }, [history, blocks, activeBlockId, getCurrentCursorPosition, restoreCursorInBlock, onChange]);

  // Handle select all blocks
  const handleSelectAllBlocks = useCallback(() => {
    if (!blocksContainerRef.current) return;
    
    try {
      // Get all contenteditable elements in order
      const editables = blocksContainerRef.current.querySelectorAll('[contenteditable]');
      if (editables.length === 0) return;
      
      const firstEditable = editables[0];
      const lastEditable = editables[editables.length - 1];
      
      // Create a range spanning all blocks
      const range = document.createRange();
      
      // Set start at the beginning of the first block
      if (firstEditable.childNodes.length > 0) {
        range.setStart(firstEditable, 0);
      } else {
        range.setStart(firstEditable, 0);
      }
      
      // Set end at the end of the last block
      if (lastEditable.childNodes.length > 0) {
        range.setEnd(lastEditable, lastEditable.childNodes.length);
      } else {
        range.setEnd(lastEditable, 0);
      }
      
      // Apply the selection
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
    } catch (error) {
      console.error('Error selecting all blocks:', error);
    }
  }, []);

  // Listen for select all blocks event
  useEffect(() => {
    const handleSelectAllEvent = (e: Event) => {
      handleSelectAllBlocks();
    };
    
    const container = blocksContainerRef.current;
    if (container) {
      container.addEventListener('selectAllBlocks', handleSelectAllEvent);
      
      return () => {
        container.removeEventListener('selectAllBlocks', handleSelectAllEvent);
      };
    }
  }, [handleSelectAllBlocks]);

  // Attach document-level keyboard handler for undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if we're in the editor (check if any contenteditable is focused or active)
      const activeElement = document.activeElement;
      const isInEditor = activeElement?.closest('[data-block-id]') !== null;
      
      if (!isInEditor) return;
      
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const isCtrlOrCmd = isMac ? e.metaKey : e.ctrlKey;
      
      // Handle Ctrl+Z / Cmd+Z for undo
      if (isCtrlOrCmd && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        handleUndo();
        return;
      }
      
      // Handle Ctrl+Shift+Z / Cmd+Shift+Z or Ctrl+Y for redo
      if ((isCtrlOrCmd && e.key === 'z' && e.shiftKey) || (isCtrlOrCmd && e.key === 'y')) {
        e.preventDefault();
        e.stopPropagation();
        handleRedo();
        return;
      }
    };
    
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [handleUndo, handleRedo]);

  // Handle content changes in any block
  const handleContentChange = useCallback((id: string, content: string) => {
    // #region agent log
    if(import.meta.env.DEV){fetch('http://127.0.0.1:7242/ingest/8fd71a2b-62c7-4902-9a51-278ccbdfc26a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Editor.tsx:handleContentChange',message:'block content changing',data:{blockId:id,contentLen:content?.length,contentSnippet:content?.substring(0,150),hasVarRef:content?.includes('var-ref'),hasDecoration:content?.includes('data-ref-key'),callerStack:new Error().stack?.split('\n').slice(1,4).join(' | ')},timestamp:Date.now(),hypothesisId:'H-D'})}).catch(()=>{});}
    // #endregion
    setBlocks(prev => {
      const newBlocks = prev.map(block => 
        block.id === id ? { ...block, content } : block
      );
      onChange?.(newBlocks);
      return newBlocks;
    });
  }, [onChange]);

  // Handle Enter key - split text at cursor position and create a new block
  const handleEnter = useCallback((id: string, contentBeforeCursor?: string, contentAfterCursor?: string) => {
    // #region agent log
    if(import.meta.env.DEV){fetch('http://127.0.0.1:7242/ingest/8fd71a2b-62c7-4902-9a51-278ccbdfc26a',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Editor.tsx:handleEnter',message:'ENTER pressed - adding new block',data:{blockId:id,contentBefore:contentBeforeCursor?.substring(0,80),contentAfter:contentAfterCursor?.substring(0,80),currentBlockCount:blocks?.length,callerStack:new Error().stack?.split('\n').slice(1,5).join(' | ')},timestamp:Date.now(),hypothesisId:'H-B'})}).catch(()=>{});}
    // #endregion
    // Push current state to history immediately (structural change)
    const cursorPosition = getCurrentCursorPosition();
    history.pushState(blocks, activeBlockId, cursorPosition, true);
    
    const currentIndex = blocks.findIndex(block => block.id === id);
    const newBlock: Block = {
      id: generateId(),
      type: 'text',
      content: contentAfterCursor || ''  // New block gets content after cursor
    };
    
    setBlocks(prev => {
      const newBlocks = [...prev];
      
      // Update current block with content before cursor (if provided)
      if (contentBeforeCursor !== undefined) {
        newBlocks[currentIndex] = {
          ...newBlocks[currentIndex],
          content: contentBeforeCursor
        };
      }
      
      // Insert new block after current one
      newBlocks.splice(currentIndex + 1, 0, newBlock);
      onChange?.(newBlocks);
      return newBlocks;
    });
    
    // Use setTimeout to ensure the DOM updates are complete before setting focus
    setTimeout(() => {
      setActiveBlockId(newBlock.id);
    }, 0);
  }, [blocks, onChange, history, activeBlockId, getCurrentCursorPosition]);

  // Handle Add Block button - create a new block below the current one
  const handleAddBlock = useCallback((id: string) => {
    // Push current state to history immediately (structural change)
    const cursorPosition = getCurrentCursorPosition();
    history.pushState(blocks, activeBlockId, cursorPosition, true);
    
    const currentIndex = blocks.findIndex(block => block.id === id);
    const newBlock: Block = {
      id: generateId(),
      type: 'text',
      content: ''
    };
    
    setBlocks(prev => {
      const newBlocks = [...prev];
      newBlocks.splice(currentIndex + 1, 0, newBlock);
      
      // Add empty text block below if the next block is not a text block
      const nextBlock = newBlocks[currentIndex + 2]; // +2 because we just added a block
      if (!nextBlock || nextBlock.type !== 'text') {
        const emptyTextBlock: Block = {
          id: generateId(),
          type: 'text',
          content: ''
        };
        newBlocks.splice(currentIndex + 2, 0, emptyTextBlock);
      }
      
      onChange?.(newBlocks);
      return newBlocks;
    });
    
    // Use setTimeout to ensure the DOM updates are complete before setting focus
    setTimeout(() => {
      setActiveBlockId(newBlock.id);
    }, 0);
  }, [blocks, onChange, history, activeBlockId, getCurrentCursorPosition]);

  // Handle Backspace key - merge current block content with previous block and delete current
  const handleBackspace = useCallback((id: string, currentContent?: string) => {
    if (blocks.length === 1) return; // Don't delete the last block
    
    const currentIndex = blocks.findIndex(block => block.id === id);
    if (currentIndex === 0) return; // Don't delete the first block
    
    // Push current state to history immediately (structural change)
    const cursorPosition = getCurrentCursorPosition();
    history.pushState(blocks, activeBlockId, cursorPosition, true);
    
    const prevBlock = blocks[currentIndex - 1];
    const prevBlockContent = prevBlock.content || '';
    
    // Calculate the cursor position - should be at the end of the previous block's original content
    // This is where the user was effectively "joining" from
    // For HTML content, we need to count text length without tags
    const textLengthBeforeMerge = prevBlockContent.replace(/<[^>]*>/g, '').length;
    
    setBlocks(prev => {
      let newBlocks = [...prev];
      
      // Merge current block's content with previous block's content
      if (currentContent && currentContent.trim() !== '' && prevBlock.type === 'text') {
        // Append current content to the end of previous block
        newBlocks[currentIndex - 1] = {
          ...newBlocks[currentIndex - 1],
          content: prevBlockContent + currentContent
        };
      }
      
      // Remove current block
      newBlocks = newBlocks.filter(block => block.id !== id);
      
      // Ensure there's always at least one block
      if (newBlocks.length === 0) {
        newBlocks = [{ id: generateId(), type: 'text', content: '' }];
      }
      
      // Ensure the last block is always a text block
      const lastBlock = newBlocks[newBlocks.length - 1];
      if (lastBlock.type !== 'text') {
        const emptyTextBlock: Block = {
          id: generateId(),
          type: 'text',
          content: ''
        };
        newBlocks.push(emptyTextBlock);
      }
      
      onChange?.(newBlocks);
      return newBlocks;
    });
    
    // Set cursor position to end of previous block's content (before merge)
    // If merging content, position at where the content was joined
    // If no content to merge (empty block deleted), position at end
    if (currentContent && currentContent.trim() !== '' && prevBlock.type === 'text') {
      // Content was merged - position cursor at the merge point
      setPendingCursorPosition({ blockId: prevBlock.id, position: textLengthBeforeMerge });
    } else {
      // Empty block deleted - position cursor at end
      setPendingCursorPosition({ blockId: prevBlock.id, position: 'end' });
    }
    
    // Focus the previous block
    setActiveBlockId(prevBlock.id);
  }, [blocks, onChange, history, activeBlockId, getCurrentCursorPosition]);

  // Handle focus events - update which block is active
  const handleFocus = useCallback((id: string) => {
    setActiveBlockId(id);
  }, []);

  // Handle arrow key navigation - move to previous block
  const handleArrowUp = useCallback((id: string) => {
    const currentIndex = blocks.findIndex(block => block.id === id);
    if (currentIndex > 0) {
      setActiveBlockId(blocks[currentIndex - 1].id);
    }
  }, [blocks]);

  // Handle arrow key navigation - move to next block
  const handleArrowDown = useCallback((id: string) => {
    const currentIndex = blocks.findIndex(block => block.id === id);
    if (currentIndex < blocks.length - 1) {
      setActiveBlockId(blocks[currentIndex + 1].id);
    }
  }, [blocks]);

  // Clear pending cursor position after it has been applied
  const handleCursorPositionApplied = useCallback(() => {
    setPendingCursorPosition(null);
  }, []);

  // Handle multiline paste - create separate blocks for each line
  const handleMultilinePaste = useCallback((id: string, lines: string[]) => {
    const currentIndex = blocks.findIndex(block => block.id === id);
    if (currentIndex === -1) return;
    
    // Update the current block with the first line
    const firstLine = lines[0];
    
    // Create new blocks for the remaining lines
    const newBlocks = lines.slice(1).map(line => ({
      id: generateId(),
      type: 'text' as const,
      content: line
    }));
    
    setBlocks(prev => {
      const updated = [...prev];
      
      // Update current block with first line
      updated[currentIndex] = { ...updated[currentIndex], content: firstLine };
      
      // Insert new blocks after current block
      updated.splice(currentIndex + 1, 0, ...newBlocks);
      
      onChange?.(updated);
      return updated;
    });
    
    // Focus the last created block
    if (newBlocks.length > 0) {
      setTimeout(() => {
        setActiveBlockId(newBlocks[newBlocks.length - 1].id);
      }, 0);
    }
  }, [blocks, onChange]);

  // Handle command input (when user types '/') - show command menu
  const handleCommand = useCallback((id: string, command: string) => {
    if (command === '/') {
      // Get cursor position for menu placement (adjusted for zoom)
      const element = document.activeElement as HTMLTextAreaElement;
      if (element) {
        const rect = element.getBoundingClientRect();
        // Divide by zoom to compensate for CSS zoom on container
        setCommandMenu({
          isOpen: true,
          blockId: id,
          position: { x: rect.left / zoom, y: (rect.bottom + 8) / zoom }
        });
      }
    }
  }, [zoom]);

  // Handle command selection from the command menu
  const handleCommandSelect = useCallback((command: string) => {
    // Push current state to history immediately (structural change - block type change)
    const cursorPosition = getCurrentCursorPosition();
    history.pushState(blocks, activeBlockId, cursorPosition, true);
    
    const blockId = commandMenu.blockId;
    const block = blocks.find(b => b.id === blockId);
    
    if (!block) return;

    // Find custom block definition
    const customBlock = customBlocks.find(cb => cb.type === command);
    
    if (command === 'image') {
      // Replace current block with image block and add empty text block below
      setBlocks(prev => {
        const currentIndex = prev.findIndex(b => b.id === blockId);
        const newBlocks = [...prev];
        
        // Replace the current block
        newBlocks[currentIndex] = { 
          ...newBlocks[currentIndex], 
          type: 'image', 
          content: '', 
          metadata: {} 
        };
        
        // Add empty text block below if it's not the last block or if the next block is not a text block
        const nextBlock = newBlocks[currentIndex + 1];
        if (!nextBlock || nextBlock.type !== 'text') {
          const emptyTextBlock: Block = {
            id: generateId(),
            type: 'text',
            content: ''
          };
          newBlocks.splice(currentIndex + 1, 0, emptyTextBlock);
        }
        
        onChange?.(newBlocks);
        return newBlocks;
      });
    } else if (command === 'table') {
      // Replace current block with table block and add empty text block below
      setBlocks(prev => {
        const currentIndex = prev.findIndex(b => b.id === blockId);
        const newBlocks = [...prev];
        
        // Replace the current block
        newBlocks[currentIndex] = { 
          ...newBlocks[currentIndex], 
          type: 'table', 
          content: '', 
          metadata: {
            tableData: {
              headers: ['Column 1', 'Column 2', 'Column 3'],
              rows: [
                [{ content: '' }, { content: '' }, { content: '' }],
                [{ content: '' }, { content: '' }, { content: '' }],
              ]
            }
          } 
        };
        
        // Add empty text block below if it's not the last block or if the next block is not a text block
        const nextBlock = newBlocks[currentIndex + 1];
        if (!nextBlock || nextBlock.type !== 'text') {
          const emptyTextBlock: Block = {
            id: generateId(),
            type: 'text',
            content: ''
          };
          newBlocks.splice(currentIndex + 1, 0, emptyTextBlock);
        }
        
        onChange?.(newBlocks);
        return newBlocks;
      });
    } else if (customBlock) {
      // Replace with custom block and add empty text block below
      setBlocks(prev => {
        const currentIndex = prev.findIndex(b => b.id === blockId);
        const newBlocks = [...prev];
        
        // Replace the current block
        newBlocks[currentIndex] = { 
          ...newBlocks[currentIndex], 
          type: command, 
          content: '', 
          metadata: customBlock.initialMetadata || {} 
        };
        
        // Add empty text block below if it's not the last block or if the next block is not a text block
        const nextBlock = newBlocks[currentIndex + 1];
        if (!nextBlock || nextBlock.type !== 'text') {
          const emptyTextBlock: Block = {
            id: generateId(),
            type: 'text',
            content: ''
          };
          newBlocks.splice(currentIndex + 1, 0, emptyTextBlock);
        }
        
        onChange?.(newBlocks);
        return newBlocks;
      });
    } else {
      // Update block type and remove the '/' character, then add empty text block below
      const newContent = block.content.slice(0, -1);
      setBlocks(prev => {
        const currentIndex = prev.findIndex(b => b.id === blockId);
        const newBlocks = [...prev];
        
        // Update the current block
        newBlocks[currentIndex] = { 
          ...newBlocks[currentIndex], 
          type: command, 
          content: newContent 
        };
        
        // Add empty text block below if it's not the last block or if the next block is not a text block
        const nextBlock = newBlocks[currentIndex + 1];
        if (!nextBlock || nextBlock.type !== 'text') {
          const emptyTextBlock: Block = {
            id: generateId(),
            type: 'text',
            content: ''
          };
          newBlocks.splice(currentIndex + 1, 0, emptyTextBlock);
        }
        
        onChange?.(newBlocks);
        return newBlocks;
      });
    }
    
    setCommandMenu({ isOpen: false, blockId: '', position: { x: 0, y: 0 } });
    toast.success(`Block converted to ${command}`);
  }, [commandMenu.blockId, blocks, customBlocks, onChange, history, activeBlockId, getCurrentCursorPosition]);

  // Handle image upload/change for image blocks
  const handleImageChange = useCallback((id: string, src: string, alt: string, size?: import('./ImageBlock').ImageSize, alignment?: import('./ImageBlock').ImageAlignment) => {
    console.log('Editor handleImageChange called:', { id, src, alt, size, alignment });
    setBlocks(prev => {
      const newBlocks = prev.map(block => 
        block.id === id 
          ? { ...block, metadata: { ...block.metadata, src, alt, size, alignment } }
          : block
      );
      console.log('Editor - Updated blocks:', newBlocks.find(b => b.id === id));
      onChange?.(newBlocks);
      return newBlocks;
    });
    toast.success('Image uploaded successfully');
  }, [onChange]);

  // Handle table data change for table blocks
  const handleTableChange = useCallback((id: string, data: import('./TableBlock').TableData) => {
    setBlocks(prev => {
      const newBlocks = prev.map(block => 
        block.id === id 
          ? { ...block, metadata: { ...block.metadata, tableData: data } }
          : block
      );
      onChange?.(newBlocks);
      return newBlocks;
    });
  }, [onChange]);

  // Handle general block updates (used by custom blocks)
  const handleBlockUpdate = useCallback((id: string, updates: Partial<Block>) => {
    setBlocks(prev => {
      const newBlocks = prev.map(block => 
        block.id === id ? { ...block, ...updates } : block
      );
      onChange?.(newBlocks);
      return newBlocks;
    });
  }, [onChange]);

  // Handle metadata changes (e.g., highlight exclusions)
  const handleMetadataChange = useCallback((id: string, metadata: Record<string, any>) => {
    setBlocks(prev => {
      const newBlocks = prev.map(block => 
        block.id === id ? { ...block, metadata: { ...block.metadata, ...metadata } } : block
      );
      onChange?.(newBlocks);
      return newBlocks;
    });
  }, [onChange]);

  // Helper to find which block an element or node belongs to
  const findBlockIdFromElement = useCallback((node: Node | Element | null): string | null => {
    if (!node) return null;
    
    // If it's a text node, get its parent element
    let element: Element | null = null;
    if (node.nodeType === Node.TEXT_NODE) {
      element = node.parentElement;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      element = node as Element;
    }
    
    if (!element) return null;
    
    // Look for the closest sortable block container
    const blockContainer = element.closest('[data-block-id]');
    if (blockContainer) {
      return blockContainer.getAttribute('data-block-id');
    }
    return null;
  }, []);

  // Helper function to get text before a node
  const getTextBeforeNode = useCallback((container: Node, targetNode: Text, offset: number): string => {
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
      null
    );

    let text = '';
    let node;
    while ((node = walker.nextNode()) !== null) {
      if (node === targetNode) {
        text += (node.textContent || '').substring(0, offset);
        break;
      }
      text += node.textContent || '';
    }
    return text;
  }, []);

  // Helper function to wrap content with formatting
  const wrapWithFormatting = useCallback((content: string, command: string, value?: string): string => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;

    let wrappedContent = content;

    switch (command) {
      case 'bold':
        wrappedContent = `<strong>${content}</strong>`;
        break;
      case 'italic':
        wrappedContent = `<em>${content}</em>`;
        break;
      case 'underline':
        wrappedContent = `<u>${content}</u>`;
        break;
      case 'strikethrough':
        wrappedContent = `<s>${content}</s>`;
        break;
      case 'foreColor':
        if (value) {
          wrappedContent = `<span style="color: ${value}">${content}</span>`;
        }
        break;
      case 'hiliteColor':
        if (value) {
          wrappedContent = `<span style="background-color: ${value}">${content}</span>`;
        }
        break;
      case 'formatBlock':
        if (value) {
          wrappedContent = `<${value}>${content}</${value}>`;
        }
        break;
      default:
        wrappedContent = content;
    }

    return wrappedContent;
  }, []);

  // Helper function to wrap partial content
  const wrapPartialContent = useCallback((content: string, startOffset: number, endOffset: number, command: string, value?: string): string => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    const textContent = tempDiv.textContent || '';

    // Extract the parts
    const beforeText = textContent.substring(0, startOffset);
    const selectedText = textContent.substring(startOffset, endOffset);
    const afterText = textContent.substring(endOffset);

    // Wrap the selected part
    const wrappedSelection = wrapWithFormatting(selectedText, command, value);

    // Reconstruct (this is simplified - in reality we'd need to preserve HTML structure)
    // For now, we'll format the whole block if any part is selected
    return wrapWithFormatting(content, command, value);
  }, [wrapWithFormatting]);

  // Handle formatting across multiple blocks
  const handleMultiBlockFormatting = useCallback((command: string, value?: string) => {
    // Get the current selection
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) return;

    // Find which blocks are affected by the selection
    const anchorBlock = findBlockIdFromElement(selection.anchorNode);
    const focusBlock = findBlockIdFromElement(selection.focusNode);

    if (!anchorBlock || !focusBlock) return;

    // Determine the range of blocks to format
    const anchorIndex = blocks.findIndex(b => b.id === anchorBlock);
    const focusIndex = blocks.findIndex(b => b.id === focusBlock);

    if (anchorIndex === -1 || focusIndex === -1) return;

    // Get the range (handle selection in either direction)
    const startIndex = Math.min(anchorIndex, focusIndex);
    const endIndex = Math.max(anchorIndex, focusIndex);

    // If only one block is selected, return and let the normal handler deal with it
    if (startIndex === endIndex) return;

    console.log('🎨 Applying formatting across blocks:', { command, value, startIndex, endIndex });

    // Save the current selection range
    const range = selection.getRangeAt(0);
    const startContainer = range.startContainer;
    const startOffset = range.startOffset;
    const endContainer = range.endContainer;
    const endOffset = range.endOffset;
    
    // Apply formatting to each affected block
    setBlocks(prev => {
      const newBlocks = prev.map((block, index) => {
        // Only process text blocks in the selection range
        if (block.type !== 'text' || index < startIndex || index > endIndex) {
          return block;
        }

        // Get the block element
        const blockElement = document.querySelector(`[data-block-id="${block.id}"] [contenteditable]`) as HTMLDivElement;
        if (!blockElement) return block;
        
        // Temporarily enable contentEditable if needed
        const wasNotEditable = blockElement.contentEditable === 'false';
        if (wasNotEditable) {
          blockElement.contentEditable = 'true';
        }

        try {
          // Determine what part of this block to format
          let formatStart = 0;
          let formatEnd = blockElement.textContent?.length || 0;
          
          // First block - format from selection start to end
          if (index === startIndex) {
            if (startContainer.nodeType === Node.TEXT_NODE) {
              formatStart = getTextBeforeNode(blockElement, startContainer as Text, startOffset).length;
            }
          }
          
          // Last block - format from start to selection end
          if (index === endIndex) {
            if (endContainer.nodeType === Node.TEXT_NODE) {
              formatEnd = getTextBeforeNode(blockElement, endContainer as Text, endOffset).length;
            }
          }
          
          // Select the text to format within this block
          const walker = document.createTreeWalker(
            blockElement,
            NodeFilter.SHOW_TEXT,
            null
          );
          
          let currentPos = 0;
          let startNode: Node | null = null;
          let endNode: Node | null = null;
          let startNodeOffset = 0;
          let endNodeOffset = 0;
          
          let node;
          while ((node = walker.nextNode()) !== null) {
            const nodeLength = node.textContent?.length || 0;
            
            // Find start node
            if (!startNode && currentPos + nodeLength > formatStart) {
              startNode = node;
              startNodeOffset = formatStart - currentPos;
            }
            
            // Find end node
            if (!endNode && currentPos + nodeLength >= formatEnd) {
              endNode = node;
              endNodeOffset = formatEnd - currentPos;
              break;
            }
            
            currentPos += nodeLength;
          }
          
          // Apply formatting if we found the range
          if (startNode && endNode) {
            const blockRange = document.createRange();
            blockRange.setStart(startNode, startNodeOffset);
            blockRange.setEnd(endNode, endNodeOffset);
            
            const sel = window.getSelection();
            if (sel) {
              sel.removeAllRanges();
              sel.addRange(blockRange);
              
              // Apply the formatting command
              if (command === 'foreColor' && value) {
                document.execCommand('styleWithCSS', false, 'true');
                document.execCommand('foreColor', false, value);
              } else if (command === 'hiliteColor' && value) {
                document.execCommand('hiliteColor', false, value);
              } else if (command === 'formatBlock' && value) {
                document.execCommand('formatBlock', false, value);
              } else {
                document.execCommand(command, false, value);
              }
            }
          }
          
          // Get updated content
          const updatedContent = blockElement.innerHTML;
          
          // Restore contentEditable state
          if (wasNotEditable) {
            blockElement.contentEditable = 'false';
          }
          
          return {
            ...block,
            content: updatedContent
          };
        } catch (error) {
          console.error('Error formatting block:', block.id, error);
          // Restore contentEditable state on error
          if (wasNotEditable) {
            blockElement.contentEditable = 'false';
          }
          return block;
        }
      });

      onChange?.(newBlocks);
      return newBlocks;
    });

    // Clear the selection after a short delay
    setTimeout(() => {
      if (selection) {
        selection.removeAllRanges();
      }
    }, 10);
  }, [blocks, findBlockIdFromElement, onChange, getTextBeforeNode]);


  // Handle block deletion
  const handleDelete = useCallback((id: string) => {
    if (blocks.length === 1) return; // Don't delete the last block
    
    // Push current state to history immediately (structural change)
    const cursorPosition = getCurrentCursorPosition();
    history.pushState(blocks, activeBlockId, cursorPosition, true);
    
    // Clear highlight exclusions for this block
    manualHighlightExclusionsService.clearBlockExclusions(id);
    
    const currentIndex = blocks.findIndex(block => block.id === id);
    const nextBlock = blocks[currentIndex + 1] || blocks[currentIndex - 1];
    
    setBlocks(prev => {
      let newBlocks = prev.filter(block => block.id !== id);
      
      // Ensure there's always at least one block
      if (newBlocks.length === 0) {
        newBlocks = [{ id: generateId(), type: 'text', content: '' }];
      }
      
      // Ensure the last block is always a text block
      const lastBlock = newBlocks[newBlocks.length - 1];
      if (lastBlock.type !== 'text') {
        const emptyTextBlock: Block = {
          id: generateId(),
          type: 'text',
          content: ''
        };
        newBlocks.push(emptyTextBlock);
      }
      
      onChange?.(newBlocks);
      return newBlocks;
    });
    
    if (nextBlock) {
      setActiveBlockId(nextBlock.id);
    }
    toast.success('Block deleted');
  }, [blocks, onChange, history, activeBlockId, getCurrentCursorPosition]);

  // Handle drag start - set active block for preview
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const block = blocks.find(b => b.id === active.id);
    setActiveBlock(block || null);
  };

  // Handle drag-and-drop reordering of blocks
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      // Push current state to history immediately (structural change - reordering)
      const cursorPosition = getCurrentCursorPosition();
      history.pushState(blocks, activeBlockId, cursorPosition, true);
      
      setBlocks((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        const newBlocks = arrayMove(items, oldIndex, newIndex);
        onChange?.(newBlocks);
        return newBlocks;
      });
    }
    
    // Clear active block when drag ends
    setActiveBlock(null);
  };

  // Handle mouse down for cross-block selection tracking
  const handleContainerMouseDown = useCallback((e: React.MouseEvent) => {
    const target = e.target as Element;
    const blockId = findBlockIdFromElement(target);
    
    if (blockId) {
      setMultiBlockSelection({
        isSelecting: true,
        isDragging: false,
        startBlockId: blockId,
        endBlockId: blockId,
        startPosition: { x: e.clientX, y: e.clientY },
      });
    }
  }, [findBlockIdFromElement]);

  // Handle mouse move for cross-block selection
  const handleContainerMouseMove = useCallback((e: React.MouseEvent) => {
    if (!multiBlockSelection.isSelecting) return;
    
    const target = e.target as Element;
    const blockId = findBlockIdFromElement(target);
    
    // Check if moved enough to be considered dragging (threshold: 5px)
    const startPos = multiBlockSelection.startPosition;
    if (startPos) {
      const dx = Math.abs(e.clientX - startPos.x);
      const dy = Math.abs(e.clientY - startPos.y);
      const isDragging = dx > 5 || dy > 5;
      
      // Check if dragging across different blocks
      const crossingBlocks = blockId && blockId !== multiBlockSelection.startBlockId;
      
      if (isDragging || crossingBlocks) {
        setMultiBlockSelection(prev => ({
          ...prev,
          isDragging: true,
          endBlockId: blockId || prev.endBlockId,
        }));
      }
    }
    
    if (blockId && blockId !== multiBlockSelection.endBlockId) {
      setMultiBlockSelection(prev => ({
        ...prev,
        endBlockId: blockId,
      }));
    }
  }, [multiBlockSelection.isSelecting, multiBlockSelection.endBlockId, multiBlockSelection.startBlockId, multiBlockSelection.startPosition, findBlockIdFromElement]);

  // Handle mouse up to finalize selection
  const handleContainerMouseUp = useCallback(() => {
    // Check if there's a selection when we release
    const selection = window.getSelection();
    const hasSelection = selection && !selection.isCollapsed;
    
    setMultiBlockSelection(prev => ({
      ...prev,
      isSelecting: false,
      // Keep isDragging state if there's an actual selection so blocks stay non-editable
      // until the user clicks elsewhere
      isDragging: hasSelection ? prev.isDragging : false,
    }));
  }, []);
  
  // Clear selection mode when clicking without selection
  const handleContainerClick = useCallback((e: React.MouseEvent) => {
    // If we were in selection mode but user clicked (not dragged), exit selection mode
    if (multiBlockSelection.isDragging) {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed) {
        setMultiBlockSelection(prev => ({
          ...prev,
          isDragging: false,
          startBlockId: null,
          endBlockId: null,
        }));
      }
    }
  }, [multiBlockSelection.isDragging]);

  // Handle copy event for multi-block selection
  useEffect(() => {
    const handleCopy = (e: ClipboardEvent) => {
      // Get the native selection
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed) return;
      
      // Check if the selection is within our editor container
      if (!blocksContainerRef.current?.contains(selection.anchorNode)) return;
      
      // Check if the selection spans multiple blocks
      const anchorBlock = findBlockIdFromElement(selection.anchorNode);
      const focusBlock = findBlockIdFromElement(selection.focusNode);
      
      // If selection spans multiple blocks, let browser handle it naturally
      // since we disabled contentEditable during selection
      if (anchorBlock && focusBlock && anchorBlock !== focusBlock) {
        // The browser should handle this correctly now since blocks are not contentEditable
        // during selection mode. We can optionally enhance the text here.
        
        // Get the selected text directly from the selection
        const selectedText = selection.toString();
        
        if (selectedText) {
          e.preventDefault();
          e.clipboardData?.setData('text/plain', selectedText);
        }
      }
    };
    
    document.addEventListener('copy', handleCopy);
    return () => document.removeEventListener('copy', handleCopy);
  }, [findBlockIdFromElement]);

  // Render the editor interface
  return (
    <div className="min-h-screen">
      <div className="w-4/5 max-w-6xl mx-auto px-6 pt-12 pb-[1000px]">
        

        {/* Drag-and-drop context for block reordering */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          modifiers={modifiers}
        >
          {/* Sortable context for managing sortable items */}
          <SortableContext
            items={blocks.map(b => b.id)}
            strategy={verticalListSortingStrategy}
          >
            {/* Container for all blocks - supports cross-block text selection */}
            <div 
              ref={blocksContainerRef}
              className="space-y-3"
              style={{ userSelect: 'text' }}
              onMouseDown={handleContainerMouseDown}
              onMouseMove={handleContainerMouseMove}
              onMouseUp={handleContainerMouseUp}
              onMouseLeave={handleContainerMouseUp}
              onClick={handleContainerClick}
            >
              {blocks.map((block) => (
                <div key={block.id} data-block-id={block.id}>
                  <SortableBlock
                    block={block}
                    isActive={activeBlockId === block.id}
                    editable={editable && !isInSelectionMode}
                    onContentChange={handleContentChange}
                    onEnter={handleEnter}
                    onFocus={handleFocus}
                    onArrowUp={handleArrowUp}
                    onArrowDown={handleArrowDown}
                    onCommand={handleCommand}
                    onImageChange={handleImageChange}
                    onTableChange={handleTableChange}
                    onDelete={handleDelete}
                    onBlockUpdate={handleBlockUpdate}
                    onAddBlock={handleAddBlock}
                    onMetadataChange={handleMetadataChange}
                    onMultiBlockFormatting={handleMultiBlockFormatting}
                    gameId={gameId}
                    customBlocks={customBlocks}
                    onDocumentContentUpdate={onDocumentContentUpdate}
                    onDocumentSave={onDocumentSave}
                    onRemoveBlock={onDocumentBlockDelete}
                    onDocumentBlockCancel={onDocumentBlockCancel}
                    onBackspace={handleBackspace}
                    onMultilinePaste={handleMultilinePaste}
                    initialCursorPosition={pendingCursorPosition?.blockId === block.id ? pendingCursorPosition.position : undefined}
                    onCursorPositionApplied={handleCursorPositionApplied}
                    enableVariableReferences={enableVariableReferences}
                    variableTypes={variableTypes}
                    variableObjects={variableObjects}
                  />
                </div>
              ))}
            </div>
          </SortableContext>
          
          <DragOverlay>
            {activeBlock ? (
              <div className="opacity-90 transform rotate-2 shadow-2xl pointer-events-none">
                <SortableBlock
                  block={activeBlock}
                  isActive={false}
                  onContentChange={() => {}}
                  onEnter={() => {}}
                  onBackspace={() => {}}
                  onFocus={() => {}}
                  onArrowUp={() => {}}
                  onArrowDown={() => {}}
                  onCommand={() => {}}
                  onImageChange={() => {}}
                  onTableChange={() => {}}
                  onDelete={() => {}}
                  onBlockUpdate={() => {}}
                  onAddBlock={() => {}}
                  gameId={gameId}
                  customBlocks={customBlocks}
                  onDocumentContentUpdate={() => {}}
                  onDocumentSave={() => {}}
                  onDocumentBlockCancel={() => {}}
                  onRemoveBlock={() => {}}
                />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>

        {/* Command menu that appears when typing '/' */}
        <CommandMenu
          isOpen={commandMenu.isOpen}
          onSelect={handleCommandSelect}
          onClose={() => setCommandMenu({ isOpen: false, blockId: '', position: { x: 0, y: 0 } })}
          position={commandMenu.position}
          customCommands={customBlocks}
        />
      </div>
    </div>
  );
}; 